// src/features/etf-info/EtfInfoPage.tsx
//
// ETF Info Deep-Dive Page — Component Islands architecture for progressive
// loading and failure isolation. Mirrors StockInfoPage but through the
// ETF-native quantitative lens: Assets Under Management (AUM), Expense
// Ratio, NAV, Top Holdings, Sector Exposure, Geographic Allocation, and
// competitor ETFs ranked by AUM.
//
// Extracts the symbol from the URL query param (?symbol=SPY), fetches the
// Overview island (quote + profile + static ETF info) up front, and lets
// each downstream island (chart, holdings, allocations, peers) fetch its
// own data independently so one slow/failing endpoint never blocks the page.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Sidebar } from '../../shared/ui';
import {
    resolveCurrency,
    formatMoney,
    formatCompactMoney,
    formatMarketCap,
} from '../../shared/utils/currency';
import { fetchEtfOverview, type EtfOverview } from './api';
import { IntradayChart } from './components/IntradayChart';
import { HoldingsTable } from './components/HoldingsTable';
import { AllocationCharts } from './components/AllocationCharts';
import { EtfPeersTable } from './components/EtfPeersTable';
import { BreakoutAlertWidget } from '../breakout-alerts/components/BreakoutAlertWidget';
import { usePortfolioStore } from '../user-portfolios/store/usePortfolioStore';

export const EtfInfoPage = () => {
    const navigate = useNavigate();
    // Zustand: opens the global "Add to Portfolio" modal (isolated re-render).
    const openModal = usePortfolioStore((s) => s.openModal);

    const [searchParams] = useSearchParams();
    const symbol = searchParams.get('symbol')?.toUpperCase();

    const [overview, setOverview] = useState<EtfOverview | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!symbol) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchEtfOverview(symbol)
            .then((data) => {
                if (!cancelled) setOverview(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setError(`Unable to load data for ${symbol}. Please try again.`);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    // Resolve the listing currency ONCE and thread it through every money
    // display. For an ETF the fund's NAV currency is the final authority (a
    // GBP-reporting fund quoted intraday in USD should still render in GBP), so
    // it DOMINATES; we then fall back to the quote currency, then the
    // symbol/exchange heuristics.
    const currency = resolveCurrency({
        navCurrency: overview?.info?.nav_currency,
        explicitCurrency: overview?.quote?.currency,
        symbol,
        exchange: overview?.quote?.exchange ?? overview?.profile?.exchange,
    });

    // Formatters for the institutional look.
    const formatCurrency = (val?: number) =>
        formatMoney(val, { explicitCurrency: currency }, { fallback: 'N/A' });
    const formatCompact = (val?: number) =>
        formatCompactMoney(val, { explicitCurrency: currency }, { fallback: 'N/A' });
    const formatPercent = (val?: number) =>
        val != null ? `${val > 0 ? '+' : ''}${val.toFixed(2)}%` : 'N/A';

    if (!symbol) {
        return (
            <Layout sidebar={<Sidebar />} title="ETF Intelligence">
                <div className="p-6 text-slate-500 dark:text-slate-400">
                    No symbol provided. Please select an ETF from the Screener.
                </div>
            </Layout>
        );
    }

    const fundName =
        overview?.info?.name ||
        overview?.profile?.company_name ||
        overview?.quote?.name ||
        symbol;

    return (
        <Layout
            sidebar={<Sidebar />}
            title={`ETF Deep Dive: ${symbol}`}
            subtitle={fundName}
        >
            <div className="mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                    &larr; Back to Rankings
                </button>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse">
                    Loading fund data for {symbol}…
                </div>
            ) : (
                overview && (
                    <div className="space-y-6">
                        {/* --- HEADER ISLAND: Identity & Live Quote --- */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 border-l-4 border-l-blue-500">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {fundName}
                                        </h1>
                                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-mono font-bold rounded border border-slate-200 dark:border-slate-700">
                                            {overview.quote.exchange || 'N/A'} : {symbol}
                                        </span>
                                    </div>
                                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                                        {[
                                            overview.info.etf_company,
                                            overview.info.asset_class,
                                            overview.info.domicile,
                                        ]
                                            .filter(Boolean)
                                            .join(' • ') || 'Exchange-Traded Fund'}
                                    </p>
                                </div>

                                <div className="mt-4 md:mt-0 text-left md:text-right">
                                    <div className="text-4xl font-mono font-bold text-slate-900 dark:text-white">
                                        {formatCurrency(overview.quote.price)}
                                    </div>
                                    <div
                                        className={`text-lg font-medium mt-1 flex items-center md:justify-end gap-1 ${
                                            (overview.quote.change ?? 0) >= 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-red-600 dark:text-red-400'
                                        }`}
                                    >
                                        {(overview.quote.change ?? 0) >= 0 ? '▲' : '▼'}
                                        {Math.abs(overview.quote.change ?? 0).toFixed(2)} (
                                        {formatPercent(overview.quote.changes_percentage)})
                                    </div>
                                    <button
                                        onClick={() =>
                                            openModal({
                                                symbol,
                                                name: fundName,
                                                type: 'ETF',
                                                sector: overview.info.asset_class ?? null,
                                                currentPrice: overview.quote.price ?? 0,
                                                exchange:
                                                    overview.quote.exchange ??
                                                    overview.profile.exchange,
                                                currency,
                                            })
                                        }
                                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
                                    >
                                        + Add to Portfolio
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* --- ACTIONABLE ALGORITHMIC BREAKOUT ALERT ISLAND --- */}
                        <BreakoutAlertWidget symbol={symbol} />

                        {/* --- KPI WIDGETS --- */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Assets Under Management
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {formatMarketCap(overview.info.aum, currency)}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Expense Ratio
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {overview.info.expense_ratio != null
                                        ? `${overview.info.expense_ratio.toFixed(2)}%`
                                        : 'N/A'}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Dividend Yield
                                </p>
                                <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-500">
                                    {overview.info.dividend_yield != null
                                        ? `${overview.info.dividend_yield.toFixed(2)}%`
                                        : 'N/A'}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Net Asset Value (NAV)
                                </p>
                                <div className="flex items-end gap-2 mt-1">
                                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                        {formatCurrency(overview.info.nav)}
                                    </p>
                                    <span className="text-sm text-slate-500 dark:text-slate-400 mb-1 font-medium">
                                        {overview.info.nav_currency || ''}
                                    </span>
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Holdings
                                </p>
                                <div className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-500">
                                    {overview.info.holdings_count != null
                                        ? overview.info.holdings_count.toLocaleString()
                                        : 'N/A'}
                                </div>
                            </div>
                        </div>

                        {/* --- ADDITIONAL STATS (Beta, Dividend, Volume, ISIN) --- */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Beta</p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {overview.profile.beta != null ? overview.profile.beta.toFixed(2) : 'N/A'}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Last Dividend</p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {formatCurrency(overview.profile.last_dividend)}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Average Volume</p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {(() => {
                                        const av = overview.quote.avg_volume ?? overview.info.avg_volume;
                                        return av != null ? av.toLocaleString() : 'N/A';
                                    })()}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">ISIN</p>
                                <div className="text-lg font-bold mt-1 text-slate-900 dark:text-white font-mono break-all">
                                    {overview.profile.isin || 'N/A'}
                                </div>
                            </div>
                        </div>

                        {/* --- FUND SNAPSHOT (full-width, directly below the KPIs) --- */}
                        <div className="surface-card flex flex-col">
                            <h2 className="section-title mb-4">Fund Snapshot</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Avg Volume
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {formatCompact(overview.info.avg_volume)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Inception
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.info.inception_date || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Issuer
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.info.etf_company || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Asset Class
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.info.asset_class || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                {overview.info.description ||
                                    overview.profile.description ||
                                    'No fund description available.'}
                            </p>
                        </div>

                        {/* --- SECTOR & GEOGRAPHIC EXPOSURE PIES --- */}
                        <AllocationCharts symbol={symbol} />

                        {/* --- INTERACTIVE CHART & PEERS GRID --- */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
                            {/* Chart gets 2/3 width on large screens */}
                            <div className="xl:col-span-2">
                                <IntradayChart symbol={symbol} currency={currency} />
                            </div>

                            {/* Peers gets 1/3 width */}
                            <div className="xl:col-span-1 h-[440px] xl:h-auto">
                                <EtfPeersTable symbol={symbol} currency={currency} />
                            </div>
                        </div>

                        {/* --- TOP HOLDINGS (full-width, below the chart + peers row) --- */}
                        <HoldingsTable symbol={symbol} currency={currency} />
                    </div>
                )
            )}
        </Layout>
    );
};

export default EtfInfoPage;
